import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Layers, CheckCircle, Zap, Shield } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackProductPageView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

import IsometricCalendar from '../components/IsometricCalendar';

const CalendarProduct: React.FC = () => {
  usePageTitle('Calendar & Scheduling for Veterinary Practices');

  // Track sales link clicks
  useSalesTracking();

  useEffect(() => {
    trackProductPageView('calendar');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                Calendar
              </span>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Visual scheduling built for veterinary clinics
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              See your entire day at a glance with swim lane views for each veterinarian. Seamlessly manage appointments, track patient flow, and coordinate your team. Works perfectly with our <Link to="/products/ai-front-desk" className="text-blue-600 hover:text-blue-700 underline">AI Front Desk</Link> and <Link to="/products/online-booking" className="text-blue-600 hover:text-blue-700 underline">Online Booking</Link>.
            </p>

            <div className="flex gap-4 mb-8">
              <Link
                to="/signup"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Request demo →
              </Link>
            </div>
          </div>

          <div className="relative h-[400px] w-full flex items-center justify-center bg-white rounded-3xl overflow-hidden">
            <IsometricCalendar />
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Layers className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Swim Lane Views</h3>
              <p className="text-sm text-gray-600">See all vets' schedules side-by-side with color-coded appointments for instant visibility.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Patient Flow Tracking</h3>
              <p className="text-sm text-gray-600">Track patients from check-in through exam to checkout with real-time status updates.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Drag & Drop Scheduling</h3>
              <p className="text-sm text-gray-600">Quickly reschedule appointments by dragging them to new time slots or vets.</p>
            </div>
          </div>
        </div>

        {/* Swim Lane Views Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Swim lane scheduling</h2>
          <p className="text-lg text-gray-600 mb-8">
            See every veterinarian's schedule in parallel columns. Perfect for multi-vet practices where you need to coordinate rooms and staff efficiently.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Swim lanes for each staff member</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Visual duration blocks show appointment length at a glance</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">See conflicts and gaps instantly</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Filter by date range or specific veterinarian</span>
                </div>
              </div>

              <div className="flex justify-center">
                <img
                  src="/swim.jpg"
                  alt="Swim lane calendar view showing multiple veterinarians' schedules side by side"
                  className="rounded-lg shadow-lg max-w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Patient Flow Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Real-time patient flow</h2>
          <p className="text-lg text-gray-600 mb-8">
            Track every patient through your clinic workflow: Waiting → Checked In → In Progress → Complete. Your entire team stays synchronized without walkie-talkies or shouting across hallways.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 mb-3">Front desk view:</h3>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Check in patients as they arrive</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">See which rooms are occupied</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Track wait times automatically</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 mb-3">Vet view:</h3>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">See which patients are ready</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Update status from exam room</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Access patient records instantly</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <img
                  src="/flow.jpg"
                  alt="Patient flow board showing appointment status from waiting to completion"
                  className="rounded-lg shadow-lg max-w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Integration Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete practice automation</h2>
          <p className="text-lg text-gray-600 mb-8">
            Calendar integrates seamlessly with the rest of the {APP_NAME} platform for end-to-end practice management.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">AI Front Desk </span>
                  <span className="text-gray-700">books appointments directly to your calendar</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">Online Booking </span>
                  <span className="text-gray-700">shows real-time availability from your calendar</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">Medical Records </span>
                  <span className="text-gray-700">links encounters directly to scheduled appointments</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">Automated reminders </span>
                  <span className="text-gray-700">send to clients before their appointments</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Complete Your Practice Automation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/products/medical-records"
              className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all group"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                Medical Records Beta
              </h3>
              <p className="text-gray-600 mb-4">
                World's fully integrated veterinary medical records system. Link encounters directly to your scheduled appointments.
              </p>
              <span className="text-blue-600 font-medium">Learn more →</span>
            </Link>

            <Link
              to="/products/online-booking"
              className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all group"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                Online Booking
              </h3>
              <p className="text-gray-600 mb-4">
                Let clients book directly to your calendar 24/7 with real-time availability.
              </p>
              <span className="text-blue-600 font-medium">Learn more →</span>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to streamline your scheduling?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Start with a free trial. No credit card required.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Start free trial
            </Link>
            <Link
              to="/contact"
              className="bg-gray-100 text-gray-900 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarProduct;

